import React, { Fragment, useEffect, useState } from "react";
import PropTypes from "prop-types";
// MUI
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import { makeStyles } from "@material-ui/core/styles";
import {
  Checkbox,
  FormControlLabel,
  Snackbar,
  TextField,
  Typography,
} from "@material-ui/core";

// Icon
import CloseIcon from "@material-ui/icons/Close";
import EditIcon from "@material-ui/icons/Edit";

// components
import ToolTipButton from "../../components/ToolTipButton";
import Image from "../../components/Image";
import { storage } from "../../firebase/firebase";
import { create, update } from "../../db";
import { permissionError } from "../../util/alert";
import { useStateValue } from "../../store/provider";
import { Alert, AlertTitle } from "@material-ui/lab";

const useStyles = makeStyles((theme) => ({
  closeDialog: {
    position: "absolute",
    right: "10px",
    top: "5px",
  },
  bottom: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    marginTop: 10,
  },
  appBox: {
    padding: 10,
    display: "flex",
    justifyContent: "space-between",
    margin: "10px 0",
    alignItems: "end",
  },
  rowField: {
    margin: "10px 0",
  },
  head: {
    display: "flex",
    justifyContent: "center",
    marginBottom: 10,
    alignItems: "center",
    flexDirection: "column",
  },
  flex: {
    display: "flex",
    margin: "10px 0",
    justifyContent: "space-around",
    flexDirection: "column-reverse",
    alignItems: "center",
  },
  imageContainer: {
    alignItems: "center",
    display: "flex",
    justifyContent: "center",
  },
}));

const AdDialog = (props) => {
  const classes = useStyles();
  const [{ admin }] = useStateValue();

  const [open, setOpen] = useState(false);
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    website: "",
    native: "",
    interstitial: "",
    hasNative: false,
    hasInterstitial: false,
  });

  const [errors, setErrors] = useState({
    title: "",
    description: "",
    website: "",
    native: "",
    interstitial: "",
  });

  useEffect(() => {
    if (props.edit) {
      setFormData({
        title: props.ad.title,
        description: props.ad.description,
        website: props.ad.website,
        native: props.ad.native,
        interstitial: props.ad.interstitial,
        hasNative: props.ad.hasNative,
        hasInterstitial: props.ad.hasInterstitial,
      });
    }
  }, [props.edit]);

  useEffect(
    () => () => {
      if (!open && !props.edit) {
        setFormData({
          title: "",
          description: "",
          website: "",
          native: "",
          interstitial: "",
          hasNative: false,
          hasInterstitial: false,
        });
        setErrors({
          title: "",
          description: "",
          website: "",
          native: "",
          interstitial: "",
        });
      }
    },
    [open, props.edit]
  );

  const handleClose = () => {
    setOpen(false);
  };

  const handleOpen = () => {
    if (!admin.flag) return permissionError();
    setOpen(true);
  };

  const handleInputFileChange = (e) => {
    if (e.target.files[0]) {
      const fileName = new Date().getTime() + e.target.files[0].name;
      setIsImageUploading(true);
      const uploadTask = storage
        .ref(`/images/${fileName}`)
        .put(e.target.files[0]);
      uploadTask.on(
        "state_changed",
        () => {},
        (err) => console.log(err),
        async () => {
          const url = await storage
            .ref("images")
            .child(fileName)
            .getDownloadURL();

          if (e.target.name === "nativeImage")
            setFormData({
              ...formData,
              native: url,
            });
          else if (e.target.name === "interstitialImage")
            setFormData({
              ...formData,
              interstitial: url,
            });

          setIsImageUploading(false);
        }
      );
    }
  };

  const handleOnFormDataChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (
      formData.title === "" ||
      formData.description === "" ||
      formData.website === ""
    ) {
      const error = {};
      if (formData.title === "") error.title = "Invalid value!";
      if (formData.description === "") error.description = "Invalid value!";
      if (formData.website === "") error.website = "Invalid value!";
      return setErrors(error);
    }

    if (!formData.hasNative)
      if (formData.hasNative && formData.native === "")
        return setErrors({ ...errors, native: "Choose an image!" });

    if (!formData.hasInterstitial)
      if (formData.hasInterstitial && formData.interstitial === "")
        return setErrors({ ...errors, interstitial: "Choose an image!" });

    if (props.edit) {
      // edit
      await update(props.ad._id, formData, "advertisements");
      handleClose();
    } else {
      // create
      await create(
        {
          ...formData,
          show: true,
          createdAt: new Date().toISOString(),
        },
        "advertisements"
      );
      handleClose();
      // window.location.reload(true);
    }
  };

  return (
    <Fragment>
      {props.edit ? (
        <ToolTipButton onClick={handleOpen} title="Edit" placement="top">
          <EditIcon color="primary" />
        </ToolTipButton>
      ) : (
        <Button color="primary" variant="contained" onClick={handleOpen}>
          Create New Advertisement
        </Button>
      )}
      <Snackbar
        open={isImageUploading}
        autoHideDuration={6000}
        onClose={() => setIsImageUploading(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert variant="filled" severity="info" style={{ textAlign: "left" }}>
          <AlertTitle>Image Information</AlertTitle>
          Be patience your <strong>Image</strong> is uploading...
        </Alert>
      </Snackbar>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        aria-labelledby="responsive-dialog-name"
      >
        <ToolTipButton
          title="Close"
          onClick={handleClose}
          btnClass={classes.closeDialog}
        >
          <CloseIcon color="primary" />
        </ToolTipButton>
        <DialogTitle id="responsive-dialog-name">
          Advertisement Data
        </DialogTitle>

        <DialogContent>
          <form onSubmit={handleSubmit}>
            <div>
              {/* <div onClick={handleEditImage} className={classes.head}>
                <input
                  name="appImage"
                  id="appImage"
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleInputFileChange}
                />
                <Image imagePath={formData.native} />
                {errors.native && (
                  <div style={{ textAlign: "center" }}>
                    <Typography variant="caption" color="error">
                      {errors.native}
                    </Typography>
                  </div>
                )}
              </div> */}
              <div style={{ display: "flex" }}>
                <TextField
                  style={{ marginRight: 5 }}
                  className={classes.rowField}
                  value={formData.title}
                  onChange={handleOnFormDataChange}
                  error={errors.title ? true : false}
                  helperText={errors.title}
                  label="Title"
                  name="title"
                  type="text"
                  pattern="^\w+(\s+\w+)*$"
                />
                <TextField
                  style={{ marginLeft: 5 }}
                  className={classes.rowField}
                  value={formData.website}
                  onChange={handleOnFormDataChange}
                  error={errors.website ? true : false}
                  helperText={errors.website}
                  label="Website"
                  name="website"
                  type="url"
                />
              </div>
              <TextField
                className={classes.rowField}
                value={formData.description}
                onChange={handleOnFormDataChange}
                error={errors.description ? true : false}
                helperText={errors.description}
                label="Description"
                name="description"
                type="text"
                multiline
                rows={3}
                pattern="^\w+(\s+\w+)*$"
              />
            </div>

            <div className={classes.imageContainer}>
              <div className={classes.flex}>
                {formData.hasNative && (
                  <span
                    onClick={() =>
                      document.getElementById("nativeImage").click()
                    }
                  >
                    <input
                      name="nativeImage"
                      id="nativeImage"
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={handleInputFileChange}
                    />
                    <Image
                      style={{
                        width: 100,
                        height: 100,
                        objectFit: "cover",
                        borderRadius: 5,
                      }}
                      imagePath={formData.native}
                    />
                    {errors.native && (
                      <Typography variant="caption" color="error">
                        {errors.native}
                      </Typography>
                    )}
                  </span>
                )}

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.hasNative}
                      color="primary"
                      onChange={() =>
                        setFormData({
                          ...formData,
                          hasNative: !formData.hasNative,
                        })
                      }
                      inputProps={{ "aria-label": "primary checkbox" }}
                    />
                  }
                  label="Native(1200x627)"
                />
              </div>

              <div className={classes.flex}>
                {formData.hasInterstitial && (
                  <span
                    onClick={() =>
                      document.getElementById("interstitialImage").click()
                    }
                  >
                    <Image
                      style={{
                        width: 120,
                        height: 200,
                        objectFit: "cover",
                        borderRadius: 5,
                      }}
                      imagePath={formData.interstitial}
                    />
                    <input
                      name="interstitialImage"
                      id="interstitialImage"
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={handleInputFileChange}
                    />
                    {errors.interstitial && (
                      <Typography variant="caption" color="error">
                        {errors.interstitial}
                      </Typography>
                    )}
                  </span>
                )}

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.hasInterstitial}
                      color="primary"
                      onChange={() =>
                        setFormData({
                          ...formData,
                          hasInterstitial: !formData.hasInterstitial,
                        })
                      }
                      inputProps={{ "aria-label": "primary checkbox" }}
                    />
                  }
                  label="Interstitial(1080x1920)"
                />
              </div>
            </div>

            <div className={classes.bottom}>
              <Button variant="contained" color="primary" type="submit">
                Submit
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </Fragment>
  );
};

AdDialog.propTypes = {
  edit: PropTypes.bool,
  ad: PropTypes.object,
};

export default AdDialog;
